import type { TimelineFishGroup, TimelineStay } from '@/domain/fishGroups';
import { getIsoWeek } from '@/utils/date';

import type { WeekAxis, WeekColumn } from '../types';

import { buildAxisTicks, startOfIsoWeek } from './axisTicks';

export { startOfIsoWeek };

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;

/** How long a stocking registered for a later date is drawn, since none of it has elapsed. */
export const PLANNED_STAY_MS = WEEK_MS;

/** An open stay whose start is still ahead, e.g. a stocking registered in advance. */
export function isPlannedStay(stay: TimelineStay, now: Date): boolean {
    return stay.to === null && stay.from.getTime() > now.getTime();
}

/**
 * The latest instant a group's bars reach: its last stay's end, or now while it is live. A
 * planned stay has not started by now, so it reaches a fixed span past its start instead.
 */
export function groupEnd(group: TimelineFishGroup, now: Date): Date {
    const lastStay = group.stays[group.stays.length - 1];
    if (lastStay && isPlannedStay(lastStay, now)) {
        return new Date(lastStay.from.getTime() + PLANNED_STAY_MS);
    }
    if (group.isLive && (!lastStay || lastStay.to === null)) return now;
    return lastStay?.to ?? group.lifecycleEndedAt ?? now;
}

export function groupStart(group: TimelineFishGroup): Date | null {
    return group.stays[0]?.from ?? group.lifecycleStartedAt;
}

/**
 * Week columns covering every group's stays and today, Monday to Monday, and nothing more:
 * from the week of the earliest start (or today) to the week of the latest of today and every
 * group's end (a planned stay's drawn span included). A short cycle gets few weeks, which
 * widen to fill the track rather than being padded with empty ones.
 */
export function buildWeekAxis(groups: TimelineFishGroup[], now: Date): WeekAxis {
    const starts = groups.map(groupStart).filter((date): date is Date => date !== null);
    const ends = groups.map((group) => groupEnd(group, now));

    // Today stays on the board even when every group starts in a later week.
    const first = Math.min(now.getTime(), ...starts.map((d) => d.getTime()));
    const last = Math.max(now.getTime(), ...ends.map((d) => d.getTime()));

    const startMs = startOfIsoWeek(new Date(first)).getTime();
    const lastWeekStart = startOfIsoWeek(new Date(last)).getTime();
    const weekCount = Math.round((lastWeekStart - startMs) / WEEK_MS) + 1;

    const mondays: number[] = [];
    const cursor = new Date(startMs);
    for (let i = 0; i <= weekCount; i += 1) {
        mondays.push(cursor.getTime());
        cursor.setDate(cursor.getDate() + 7);
    }
    const endMs = mondays[mondays.length - 1];
    const span = endMs - startMs;

    const weeks: WeekColumn[] = mondays.slice(0, -1).map((monday, index) => ({
        key: String(monday),
        week: getIsoWeek(new Date(monday)),
        leftPct: ((monday - startMs) / span) * 100,
        widthPct: ((mondays[index + 1] - monday) / span) * 100,
    }));

    // A cycle that ended before today opens on its last weeks, not on the empty ones since.
    const lastEnd = ends.length > 0 ? Math.max(...ends.map((d) => d.getTime())) : now.getTime();
    const anchorMs = Math.min(now.getTime(), lastEnd);

    return { startMs, endMs, anchorMs, weeks, ticks: buildAxisTicks(startMs, endMs) };
}

/** Where `date` sits on the axis, as a percentage, clamped to the board. */
export function positionOf(axis: WeekAxis, date: Date): number {
    const pct = ((date.getTime() - axis.startMs) / (axis.endMs - axis.startMs)) * 100;
    return Math.min(100, Math.max(0, pct));
}

export function isOnAxis(axis: WeekAxis, date: Date): boolean {
    const ms = date.getTime();
    return ms >= axis.startMs && ms <= axis.endMs;
}

export function isSameDay(a: Date, b: Date): boolean {
    return (
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate()
    );
}
